import React from 'react'
import * as Sentry from '@sentry/react'

import type { AuthUser } from './api/auth'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined

const parseSampleRate = (value: string | undefined, fallback: number) => {
  if (!value) {
    return fallback
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return fallback
  }

  return parsed
}

export const isSentryEnabled = Boolean(sentryDsn)

export const initializeSentry = () => {
  if (!sentryDsn) {
    return
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ],
    tracesSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
      import.meta.env.PROD ? 0.1 : 1
    )
  })
}

export const syncSentryUser = (user: AuthUser | null) => {
  if (!isSentryEnabled) {
    return
  }

  if (!user) {
    Sentry.setUser(null)
    return
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.displayName ?? user.email
  })

  if (typeof user.role === 'string' && user.role.length > 0) {
    Sentry.setTag('dashboard.role', user.role)
  }

  if (user.operatorId) {
    Sentry.setTag('dashboard.operator_id', user.operatorId)
  }
}
